import { Editor } from '@tiptap/react';
import {
  IconBold,
  IconItalic,
  IconStrikethrough,
  IconCode,
  IconH1,
  IconH2,
  IconH3,
  IconList,
  IconListNumbers,
  IconBlockquote,
  IconBraces,
  IconLink,
  IconUnderline,
  IconSeparator,
  IconRotateClockwise,
  IconRotate,
  IconClearAll,
} from '@tabler/icons-react';
import { ActionIcon, Group, Divider, Tooltip } from '@mantine/core';
import './TipTapToolbar.css';

interface TipTapToolbarProps {
  editor: Editor | null;
}

export default function TipTapToolbar({ editor }: TipTapToolbarProps) {
  if (!editor) {
    return null;
  }

  const ButtonGroup = ({ children }: { children: React.ReactNode }) => (
    <Group gap={0} className="tiptap-button-group">
      {children}
    </Group>
  );

  const ToolbarButton = ({
    onClick,
    isActive,
    icon: Icon,
    label,
    disabled = false,
  }: {
    onClick: () => void;
    isActive?: boolean;
    icon: any;
    label: string;
    disabled?: boolean;
  }) => (
    <Tooltip label={label}>
      <ActionIcon
        onClick={onClick}
        variant={isActive ? 'filled' : 'subtle'}
        color={isActive ? 'blue' : 'gray'}
        size="sm"
        className="tiptap-toolbar-button"
        disabled={disabled}
      >
        <Icon size={16} />
      </ActionIcon>
    </Tooltip>
  );

  return (
    <div className="tiptap-toolbar">
      {/* History */}
      <ButtonGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          icon={IconRotate}
          label="Undo (Ctrl+Z)"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          icon={IconRotateClockwise}
          label="Redo (Ctrl+Y)"
        />
      </ButtonGroup>

      <Divider orientation="vertical" size="xs" />

      {/* Text Formatting */}
      <ButtonGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          icon={IconBold}
          label="Bold (Ctrl+B)"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          icon={IconItalic}
          label="Italic (Ctrl+I)"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          icon={IconUnderline}
          label="Underline (Ctrl+U)"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          icon={IconStrikethrough}
          label="Strikethrough"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          icon={IconCode}
          label="Inline Code"
        />
      </ButtonGroup>

      <Divider orientation="vertical" size="xs" />

      {/* Headings */}
      <ButtonGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          icon={IconH1}
          label="Heading 1"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          icon={IconH2}
          label="Heading 2"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          icon={IconH3}
          label="Heading 3"
        />
      </ButtonGroup>

      <Divider orientation="vertical" size="xs" />

      {/* Lists */}
      <ButtonGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          icon={IconList}
          label="Bullet List"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          icon={IconListNumbers}
          label="Ordered List"
        />
      </ButtonGroup>

      <Divider orientation="vertical" size="xs" />

      {/* Blocks */}
      <ButtonGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          icon={IconBlockquote}
          label="Blockquote"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          isActive={editor.isActive('codeBlock')}
          icon={IconBraces}
          label="Code Block"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          icon={IconSeparator}
          label="Horizontal Rule"
        />
      </ButtonGroup>

      <Divider orientation="vertical" size="xs" />

      {/* Clear Formatting */}
      <ButtonGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().clearNodes().run()}
          icon={IconClearAll}
          label="Clear Formatting"
        />
      </ButtonGroup>
    </div>
  );
}
